import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  Button,
  MenuItem,
  alpha,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SendIcon from '@mui/icons-material/Send';
import { getHotelInfo } from './nexastayApi';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import { getHotelDateFormat } from '../utils/dateUtils';

function buildMapQuery(hotel) {
  const parts = [hotel?.address, hotel?.city, hotel?.pincode, hotel?.country].filter(Boolean);
  return parts.length ? encodeURIComponent(parts.join(', ')) : encodeURIComponent('India');
}

function getHotelEmail(hotel) {
  if (hotel?.contact_email) return hotel.contact_email;
  if (Array.isArray(hotel?.email_addresses) && hotel.email_addresses.length) return hotel.email_addresses[0];
  return '';
}

export default function ContactPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const [hotel, setHotel] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [checkIn, setCheckIn] = useState(() => moment().format('YYYY-MM-DD'));
  const [checkOut, setCheckOut] = useState(() => moment().add(1, 'day').format('YYYY-MM-DD'));
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getHotelInfo(hotelId);
        if (!cancelled) setHotel(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load hotel.');
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId]);

  const theme = useNexaStayTheme();
  const accent = theme.accent;
  const mainBgSx = { minHeight: '100vh', bgcolor: theme.bg };
  const textMuted = theme.textMuted ?? theme.textSecondary;
  const isLight = theme.mode === 'light';
  const buttonTextOnAccent = isLight ? theme.textPrimary : '#fff';

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const to = getHotelEmail(hotel);
    if (!to) return;
    const subject = encodeURIComponent(form.subject || `Contact from ${hotel?.name || 'website'}`);
    const body = encodeURIComponent(
      (form.message || '') +
        (form.name ? `\n\n— ${form.name}` : '') +
        (form.email ? `\n${form.email}` : '')
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  if (!hotelId) {
    return (
      <Box sx={{ ...mainBgSx, p: 3, color: theme.textPrimary }}>
        <Alert severity="warning" sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#ff9800' } }}>
          Missing hotel ID.
        </Alert>
      </Box>
    );
  }

  if (hotel === null && !error) {
    return (
      <Box sx={{ ...mainBgSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: accent }} />
      </Box>
    );
  }

  const mapQuery = buildMapQuery(hotel);
  const hasPhone = hotel?.phone_number || (Array.isArray(hotel?.mobile_numbers) && hotel.mobile_numbers.length) || hotel?.landline_number;
  const hasEmail = hotel?.contact_email || (Array.isArray(hotel?.email_addresses) && hotel.email_addresses.length);
  const hasAddress = !!(hotel?.address || hotel?.city || hotel?.pincode || hotel?.country);
  const hasAnyContact = hasPhone || hasEmail || hasAddress;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.paper,
      borderRadius: 1,
      '& fieldset': { borderColor: theme.border },
      '&:hover fieldset': { borderColor: accent },
      '&.Mui-focused fieldset': { borderWidth: 1, borderColor: accent },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
  };

  /* K7-style booking strip: dark bar, white text, min-height 58px, uppercase labels */
  const bookingStripInputSx = {
    minHeight: { xs: 52, sm: 58 },
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.06)',
      borderRadius: 0,
      minHeight: { xs: 52, sm: 58 },
      '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputBase-input': { color: '#f8fafc', fontSize: '0.9rem' },
    '& .MuiInputLabel-root': {
      color: 'rgba(248,250,252,0.86)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontSize: '0.62rem',
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiSelect-select': { color: '#f8fafc' },
    '& .MuiSvgIcon-root': { color: 'rgba(248,250,252,0.78)' },
    /* DatePicker (MUI X) */
    '& .MuiPickersInputBase-root, & .MuiPickersSectionList-root, & .MuiPickersSectionList-sectionContent': {
      color: '#f8fafc',
    },
  };

  return (
    <Box sx={{ ...mainBgSx, pb: 0, color: theme.textPrimary }}>
      {error && (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, pt: 2 }}>
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              bgcolor: theme.paper,
              color: theme.textPrimary,
              '& .MuiAlert-message': { color: theme.textPrimary },
              '& .MuiAlert-icon': { color: '#f44336' },
            }}
          >
            {error}
          </Alert>
        </Box>
      )}

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1.5, sm: 3 }, pt: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: { xs: 3, lg: 4 },
            alignItems: 'start',
          }}
        >
          {/* Left: CONTACT US / Get in touch + intro + contact blocks with outline icons */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: textMuted,
                letterSpacing: 2,
                fontWeight: 600,
                display: 'block',
                mb: 0.5,
                fontSize: '0.7rem',
              }}
            >
              Contact us
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: theme.textPrimary,
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                lineHeight: { xs: 1.3, sm: 1.2 },
              }}
            >
              Get in touch
            </Typography>
            <Typography variant="body1" sx={{ color: textMuted, lineHeight: 1.7, mb: 3 }}>
              We are here to assist with reservations, stay planning, and event queries. Reach out and our team will get back to you shortly.
            </Typography>

            <Stack spacing={2.5}>
              {hasPhone && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ color: accent, mt: 0.25 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: theme.textPrimary, fontWeight: 700, mb: 0.5 }}>
                      Reservation
                    </Typography>
                    <Stack spacing={0.25}>
                      {hotel?.phone_number && (
                        <Typography component="a" href={`tel:${hotel.phone_number.replace(/\s/g, '')}`} sx={{ color: theme.textSecondary, textDecoration: 'none', '&:hover': { color: accent } }}>
                          {hotel.phone_number}
                        </Typography>
                      )}
                      {Array.isArray(hotel?.mobile_numbers) && hotel.mobile_numbers.map((n, i) => (
                        <Typography key={i} component="a" href={`tel:${(n || '').replace(/\s/g, '')}`} sx={{ color: theme.textSecondary, textDecoration: 'none', '&:hover': { color: accent } }}>
                          {n}
                        </Typography>
                      ))}
                      {hotel?.landline_number && (
                        <Typography component="a" href={`tel:${hotel.landline_number}`} sx={{ color: theme.textSecondary, textDecoration: 'none', '&:hover': { color: accent } }}>
                          {hotel.landline_number}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}

              {hasEmail && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ color: accent, mt: 0.25 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: theme.textPrimary, fontWeight: 700, mb: 0.5 }}>
                      Email Info
                    </Typography>
                    <Stack spacing={0.25}>
                      {hotel?.contact_email && (
                        <Typography component="a" href={`mailto:${hotel.contact_email}`} sx={{ color: theme.textSecondary, textDecoration: 'none', '&:hover': { color: accent } }}>
                          {hotel.contact_email}
                        </Typography>
                      )}
                      {Array.isArray(hotel?.email_addresses) &&
                        hotel.email_addresses
                          .filter((e) => e !== hotel?.contact_email)
                          .map((e, i) => (
                            <Typography key={i} component="a" href={`mailto:${e}`} sx={{ color: theme.textSecondary, textDecoration: 'none', '&:hover': { color: accent } }}>
                              {e}
                            </Typography>
                          ))}
                    </Stack>
                  </Box>
                </Box>
              )}

              {hasAddress && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ color: accent, mt: 0.25 }}>
                    <LocationOnOutlinedIcon sx={{ fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: theme.textPrimary, fontWeight: 700, mb: 0.5 }}>
                      Address
                    </Typography>
                    <Typography variant="body1" sx={{ color: theme.textSecondary, lineHeight: 1.6 }}>
                      {[hotel?.address, hotel?.city, hotel?.pincode, hotel?.country].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {!hasAnyContact && (
                <Typography variant="body2" sx={{ color: textMuted }}>Contact information will be available soon.</Typography>
              )}
            </Stack>
          </Box>

          {/* Right: Get in touch form – Name & Email side-by-side, Subject, Message, SEND MESSAGE */}
          <Box
            component="form"
            onSubmit={handleFormSubmit}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: theme.paper,
              border: `1px solid ${theme.border}`,
              boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 700, mb: 2 }}>
              Get in touch
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  sx={inputSx}
                />
                <TextField
                  placeholder="your@example.com"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  sx={inputSx}
                />
              </Box>
              <TextField
                fullWidth
                placeholder="Subject"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                sx={inputSx}
              />
              <TextField
                fullWidth
                placeholder="Your message"
                multiline
                minRows={4}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                sx={{ ...inputSx, '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' } }}
                inputProps={{ style: { resize: 'vertical' } }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                endIcon={<SendIcon />}
                disabled={!getHotelEmail(hotel)}
                sx={{
                  bgcolor: accent,
                  color: buttonTextOnAccent,
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 1,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  '&:hover': { bgcolor: accent, opacity: 0.9 },
                }}
              >
                Send Message
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Map – full viewport width */}
      {(hotel?.address || hotel?.city || hotel?.country) && (
        <Box
          sx={{
            width: '100vw',
            maxWidth: '100%',
            marginLeft: 'calc(-50vw + 50%)',
            mt: 4,
            minHeight: 480,
            overflow: 'hidden',
          }}
        >
          <iframe
            title="Hotel location"
            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: 480, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Box>
      )}

      {/* Check Now: blurred bg; height follows form on mobile, fixed band on md+ */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: 300 },
          height: { md: 300 },
          overflow: 'hidden',
          bgcolor: theme.bg,
          marginBottom: 0,
        }}
      >
        <Box
          component="img"
          src={hotel?.image_url || hotel?.images?.[0] || 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1600&q=80'}
          alt=""
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(4px)',
            transform: 'scale(1.08)',
          }}
        />
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.25)', pointerEvents: 'none' }} />
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'auto', md: '100%' },
            height: { md: '100%' },
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            component="form"
            onSubmit={(e) => e.preventDefault()}
            sx={{
              width: '100%',
              maxWidth: 1100,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, minmax(0, 1fr))' },
              gap: { xs: 1, sm: '0.42rem' },
              p: { xs: '0.65rem', sm: '0.7rem' },
              bgcolor: 'rgba(20,22,25,0.78)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 1.5,
              alignItems: 'stretch',
            }}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Check in"
                value={checkIn ? moment(checkIn) : null}
                onChange={(d) => setCheckIn(d ? d.format('YYYY-MM-DD') : '')}
                format={getHotelDateFormat()}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: bookingStripInputSx,
                  },
                }}
              />
              <DatePicker
                label="Check out"
                value={checkOut ? moment(checkOut) : null}
                onChange={(d) => setCheckOut(d ? d.format('YYYY-MM-DD') : '')}
                minDate={checkIn ? moment(checkIn).add(1, 'day') : undefined}
                format={getHotelDateFormat()}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: bookingStripInputSx,
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              select
              size="small"
              label="Adults"
              value={adults}
              onChange={(e) => setAdults(Number(e.target.value))}
              sx={bookingStripInputSx}
            >
              <MenuItem value={1}>1 Adult</MenuItem>
              <MenuItem value={2}>2 Adults</MenuItem>
              <MenuItem value={3}>3 Adults</MenuItem>
              <MenuItem value={4}>4 Adults</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Children"
              value={children}
              onChange={(e) => setChildren(Number(e.target.value))}
              sx={bookingStripInputSx}
            >
              <MenuItem value={0}>Children</MenuItem>
              <MenuItem value={1}>1 Child</MenuItem>
              <MenuItem value={2}>2 Children</MenuItem>
              <MenuItem value={3}>3 Children</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Rooms"
              value={rooms}
              onChange={(e) => setRooms(Number(e.target.value))}
              sx={bookingStripInputSx}
            >
              <MenuItem value={1}>1 Room</MenuItem>
              <MenuItem value={2}>2 Rooms</MenuItem>
              <MenuItem value={3}>3 Rooms</MenuItem>
              <MenuItem value={4}>4 Rooms</MenuItem>
              <MenuItem value={5}>5 Rooms</MenuItem>
            </TextField>
            <Button
              component={Link}
              to={nexastayPath(basePath, hotelId)}
              variant="contained"
              disableElevation
              sx={{
                bgcolor: accent,
                color: buttonTextOnAccent,
                width: { xs: '100%', md: 'auto' },
                minHeight: { xs: 48, sm: 52, md: 58 },
                py: { xs: 1.25, md: 0 },
                px: { xs: 2, md: 1.5 },
                borderRadius: 0,
                textTransform: 'uppercase',
                letterSpacing: { xs: '0.1em', md: '0.28em' },
                fontSize: { xs: '0.8rem', sm: '0.75rem', md: '0.72rem' },
                fontWeight: 600,
                lineHeight: 1.25,
                whiteSpace: { xs: 'normal', md: 'nowrap' },
                alignSelf: 'stretch',
                '&:hover': { bgcolor: accent, opacity: 0.9 },
              }}
            >
              Check Now
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
